import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  BoardMeetingForm,
  BoardMeetingFormValues,
} from "../../components/board-meeting-form";
import { Modal } from "../../components/modal";
import { useGetAuthContext } from "../../queries/get-auth-context";
import {
  createBoardMeeting,
  fromDateTimeLocalValue,
} from "../../services/board-meeting";
import { navigateBack } from "../../utils/navigate-back";
import { recordIdToId } from "../../utils/record-id-to-id";

export function BoardMeetingCreate() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: authContext } = useGetAuthContext();

  const [loading, setLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [formValues, setFormValues] = useState<BoardMeetingFormValues>({
    name: "",
    date: "",
    location: "",
    calledIds: [],
  });

  // The caller is usually also attending, so call them by default
  useEffect(() => {
    if (authContext?.id) {
      setFormValues((values) =>
        values.calledIds.length === 0
          ? { ...values, calledIds: [authContext.id] }
          : values,
      );
    }
  }, [authContext?.id]);

  const closeModal = () => {
    navigateBack(navigate, "/admin/board-meeting/list");
  };

  return (
    <Modal open onClose={() => closeModal()}>
      <BoardMeetingForm
        hasError={hasError}
        loading={loading}
        values={formValues}
        onCancel={() => closeModal()}
        onChange={setFormValues}
        onSubmit={() => {
          (async () => {
            setLoading(true);
            try {
              const meeting = await createBoardMeeting({
                name: formValues.name.trim(),
                date: fromDateTimeLocalValue(formValues.date),
                ...(formValues.location.trim() !== ""
                  ? { location: formValues.location.trim() }
                  : {}),
                calledIds: formValues.calledIds,
              });
              await queryClient.invalidateQueries({
                queryKey: ["board-meetings"],
              });
              navigate(`/admin/board-meeting/${recordIdToId(meeting.id)}`, {
                replace: true,
              });
            } catch {
              setHasError(true);
            } finally {
              setLoading(false);
            }
          })();
        }}
      />
    </Modal>
  );
}
